'use client';

import { c as _c } from "react/compiler-runtime";
import { CountryCode, getCountryCallingCode } from 'libphonenumber-js';
import { createContext, useContext, useState } from 'react';
import { getCountryFromInternationalPhone, getCountryName, getPhoneState, PhoneFormat } from './utils';
import type { PhoneInputInputVariant, PhoneInputLayout } from './types';
export interface PhoneInputContextValue {
  actions: {
    copy: (value: string) => Promise<void>;
    setCountry: (country: CountryCode) => void;
    setCountryOpen: (open: boolean) => void;
    setValue: (value: string) => void;
  };
  meta: Record<string, never>;
  state: {
    copied: boolean;
    country?: CountryCode;
    countryName: string;
    countryOpen: boolean;
    disabled: boolean;
    formats: PhoneFormat[];
    inputVariant: PhoneInputInputVariant;
    layout: PhoneInputLayout;
    validationMessage?: string;
    value: string;
  };
}
const PhoneInputContext = createContext<null | PhoneInputContextValue>(null);
const hasSharedCallingCode = (a: CountryCode | undefined, b: CountryCode | undefined): boolean => {
  if (!a || !b) return false;
  try {
    return getCountryCallingCode(a) === getCountryCallingCode(b);
  } catch {
    return false;
  }
};
export interface PhoneInputProviderProps {
  children: React.ReactNode;
  country?: CountryCode | null;
  defaultCountry?: CountryCode;
  defaultValue?: string;
  disabled?: boolean;
  inputVariant?: PhoneInputInputVariant;
  layout?: PhoneInputLayout;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCountryChange?: (country: CountryCode) => void;
  value?: string;
}
export function PhoneInputProvider(t0) {
  const $ = _c(38);
  const {
    children,
    country: countryProp,
    defaultCountry,
    defaultValue: t1,
    disabled: t2,
    inputVariant: t3,
    layout: t4,
    onChange,
    onCountryChange,
    value: valueProp
  } = t0;
  const defaultValue = t1 === undefined ? "" : t1;
  const disabled = t2 === undefined ? false : t2;
  const inputVariant = t3 === undefined ? "default" : t3;
  const layout = t4 === undefined ? "input-group" : t4;
  const [open, setOpen] = useState(false);
  const [internalCountry, setInternalCountry] = useState(defaultCountry);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [justCopied, setJustCopied] = useState(false);
  const selectedCountry = countryProp === undefined ? internalCountry : countryProp ?? undefined;
  const currentValue = valueProp !== undefined ? String(valueProp) : internalValue;
  let t5;
  if ($[0] !== selectedCountry) {
    t5 = getCountryName(selectedCountry);
    $[0] = selectedCountry;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const countryName = t5;
  let t6;
  if ($[2] !== currentValue || $[3] !== selectedCountry) {
    t6 = getPhoneState(currentValue, selectedCountry);
    $[2] = currentValue;
    $[3] = selectedCountry;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const {
    formats,
    validationMessage
  } = t6;
  let t7;
  if ($[5] !== countryProp || $[6] !== onChange || $[7] !== onCountryChange || $[8] !== selectedCountry || $[9] !== valueProp) {
    t7 = value => {
      if (valueProp === undefined) {
        setInternalValue(value);
      }
      onChange?.({
        target: {
          value
        }
      } as React.ChangeEvent<HTMLInputElement>);
      if (value === "") {
        return;
      }
      const inferred = getCountryFromInternationalPhone(value);
      if (inferred && inferred !== selectedCountry) {
        if (hasSharedCallingCode(inferred, selectedCountry)) {
          return;
        }
        if (countryProp === undefined) {
          setInternalCountry(inferred);
        }
        onCountryChange?.(inferred);
      }
    };
    $[5] = countryProp;
    $[6] = onChange;
    $[7] = onCountryChange;
    $[8] = selectedCountry;
    $[9] = valueProp;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleChange = t7;
  let t8;
  if ($[11] !== countryProp || $[12] !== currentValue || $[13] !== onCountryChange || $[14] !== selectedCountry) {
    t8 = code => {
      const inferred_0 = getCountryFromInternationalPhone(currentValue);
      if (inferred_0 && inferred_0 !== code && !hasSharedCallingCode(inferred_0, code)) {
        if (inferred_0 !== selectedCountry) {
          if (countryProp === undefined) {
            setInternalCountry(inferred_0);
          }
          onCountryChange?.(inferred_0);
        }
        setOpen(false);
        return;
      }
      if (code !== selectedCountry) {
        onCountryChange?.(code);
        if (countryProp === undefined) {
          setInternalCountry(code);
        }
      }
      setOpen(false);
    };
    $[11] = countryProp;
    $[12] = currentValue;
    $[13] = onCountryChange;
    $[14] = selectedCountry;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleSelectCountry = t8;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = async value_0 => {
      if (typeof window === "undefined" || !navigator.clipboard?.writeText) {
        return;
      }
      try {
        await navigator.clipboard.writeText(value_0);
        setJustCopied(true);
        setTimeout(() => setJustCopied(false), 2000);
      } catch {}
    };
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const copyToClipboard = t9;
  let t10;
  if ($[17] !== handleChange || $[18] !== handleSelectCountry) {
    t10 = {
      copy: copyToClipboard,
      setCountry: handleSelectCountry,
      setCountryOpen: setOpen,
      setValue: handleChange
    };
    $[17] = handleChange;
    $[18] = handleSelectCountry;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {};
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== countryName || $[22] !== currentValue || $[23] !== disabled || $[24] !== formats || $[25] !== inputVariant || $[26] !== justCopied || $[27] !== layout || $[28] !== open || $[29] !== selectedCountry || $[30] !== validationMessage) {
    t12 = {
      copied: justCopied,
      country: selectedCountry,
      countryName,
      countryOpen: open,
      disabled,
      formats,
      inputVariant,
      layout,
      validationMessage,
      value: currentValue
    };
    $[21] = countryName;
    $[22] = currentValue;
    $[23] = disabled;
    $[24] = formats;
    $[25] = inputVariant;
    $[26] = justCopied;
    $[27] = layout;
    $[28] = open;
    $[29] = selectedCountry;
    $[30] = validationMessage;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t10 || $[33] !== t12) {
    t13 = {
      actions: t10,
      meta: t11,
      state: t12
    };
    $[32] = t10;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== children || $[36] !== t13) {
    t14 = <PhoneInputContext value={t13} data-sentry-element="PhoneInputContext" data-sentry-component="PhoneInputProvider" data-sentry-source-file="context.tsx">{children}</PhoneInputContext>;
    $[35] = children;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}
export function usePhoneInput() {
  const context = useContext(PhoneInputContext);
  if (!context) {
    throw new Error("usePhoneInput must be used within a PhoneInput");
  }
  return context;
}